import dayjs from '@/plugins/dayjs';
import parsePhoneNumber from 'libphonenumber-js';
import { IntlShape } from 'react-intl';
import { addMethod, setLocale, string } from 'yup';

declare module 'yup' {
  interface StringSchema {
    isValidAsid(): StringSchema;
    isValidCountryCode(): StringSchema;
    isValidPhone(): StringSchema;
    isValidTaxBirthdate(): StringSchema;
    isValidEmail(): StringSchema;
  }
}

const ASID_REGEX = /^(?=.*[a-zA-Z])[a-zA-Z0-9]{1,11}$/;
const COUNTRY_CODE_REGEX = /^[A-Z]{2}$/;
const VALID_EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

// Error messages for custom methods.
// Will be updated with internationalized version when it becomes available
let asidErrorMessage =
  'Must be 1-11 alphanumeric characters with at least one letter';
let countryCodeErrorMessage =
  'The country code must be valid (e.g. "US", "FR")';
let phoneErrorMessage = 'This field must be a valid phone number';
let taxBirthdateErrorMessage = 'Birthdate must be more than 10 years ago';
let emailErrorMessage = 'This field must be a valid email';

// Register custom methods immediately when module is loaded
// This ensures they're available even with eager loading
addMethod(string, 'isValidAsid', function isValidAsid() {
  return this.test({
    name: 'isValidAsid',
    message: () => asidErrorMessage,
    test: value => {
      if (value == null) {
        return true;
      }

      return ASID_REGEX.test(value);
    },
  });
});

addMethod(string, 'isValidCountryCode', function countryCode() {
  return this.test({
    name: 'isValidCountryCode',
    message: countryCodeErrorMessage,
    test: value => {
      if (value) {
        return COUNTRY_CODE_REGEX.test(value);
      }

      return true;
    },
  });
});

addMethod(string, 'isValidPhone', function isValidPhone() {
  return this.test({
    name: 'isValidPhone',
    message: () => phoneErrorMessage,
    test: value => {
      if (value) {
        const parsedPhoneNumber = parsePhoneNumber(value);
        return parsedPhoneNumber?.isPossible() || false;
      }
      return true;
    },
  });
});

addMethod(string, 'isValidTaxBirthdate', function isValidTaxBirthdate() {
  return this.test({
    name: 'isValidTaxBirthdate',
    message: () => taxBirthdateErrorMessage,
    test: value => {
      if (value) {
        const today = dayjs.utc(new Date()).subtract(10, 'year');
        return today.isAfter(value, 'day');
      }
      return true;
    },
  });
});

addMethod(string, 'isValidEmail', function isValidEmail() {
  return this.test({
    name: 'isValidEmail',
    message: () => emailErrorMessage,
    test: value => {
      // First check basic email format
      return VALID_EMAIL_REGEX.test(value || '');
    },
  });
});
export default function setupYup(intl: IntlShape) {
  // https://github.com/jquense/yup/blob/master/src/locale.ts
  setLocale({
    mixed: {
      default: intl.formatMessage({
        defaultMessage: 'This field is invalid',
        description: 'Invalid field error message',
      }),
      required: intl.formatMessage({
        defaultMessage: 'This field is required',
        description: 'Required field error message',
      }),
      notType: intl.formatMessage({
        defaultMessage: `This field's type is invalid`,
        description: 'Wrong type field error message',
      }),
    },

    string: {
      email: intl.formatMessage({
        defaultMessage: 'This field must be a valid email',
        description: 'Email field error message',
      }),
      max: params => {
        const { max } = params;

        return intl.formatMessage(
          {
            defaultMessage: 'This field must be at most {max} characters',
            description: 'Maximum length field error message',
          },
          {
            max,
          },
        );
      },
      url: intl.formatMessage({
        defaultMessage: 'This field must be a valid URL',
        description: 'URL field error message',
      }),
    },

    number: {
      integer: intl.formatMessage({
        defaultMessage: 'This field must be a positive whole number',
        description: 'Integer field error message',
      }),
      min: params => {
        const { min } = params;

        return intl.formatMessage(
          {
            defaultMessage: 'This field must be greater than or equal to {min}',
            description: 'Minimum length field error message',
          },
          {
            min,
          },
        );
      },
      max: params => {
        const { max } = params;

        return intl.formatMessage(
          {
            defaultMessage: 'This field must be less than or equal to {max}',
            description: 'Maximum length field error message',
          },
          {
            max,
          },
        );
      },
    },

    array: {
      min: params => {
        const { min } = params;

        return intl.formatMessage(
          {
            defaultMessage: 'This field must have at least {min} {item}',
            description: 'Minimum length field error message',
          },
          {
            min,
            item: min === 1 ? 'item' : 'items',
          },
        );
      },
    },
  });

  // Update the error message with internationalized version when it becomes available
  asidErrorMessage = intl.formatMessage({
    defaultMessage:
      'Must be 1-11 alphanumeric characters with at least one letter',
  });

  countryCodeErrorMessage = intl.formatMessage({
    defaultMessage: 'The country code must be valid (e.g. "US", "FR")',
  });

  phoneErrorMessage = intl.formatMessage({
    defaultMessage: 'This field must be a valid phone number',
  });

  taxBirthdateErrorMessage = intl.formatMessage({
    defaultMessage: 'Birthdate must be more than 10 years ago',
  });

  emailErrorMessage = intl.formatMessage({
    defaultMessage: 'This field must be a valid email',
  });
}
